// Логиката на редовете на доставката (hook, без рендер): state на добавените
// продукти, двупосочните изчисления (въведената стойност — цена или сума — е
// източникът на истина), авто-цена от последна доставка, валидация, суми и
// „Общо количества".
// Изнесен от DeliveryWorksheetTable (правило ≤500 реда; изрично разрешено да
// се дели на таблица + hook + карти, никога на ред/клетка).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LastPrice, Packaging, Product } from "../../../../api/inventory";
import {
  emptyDeliveryRow,
  formatUnitPrice,
  formatQtyInput,
  isRowTouched,
  parseDecimal,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";
import { formatLineSum, recalcDeliveryRow } from "./deliveryRowMath";
import { planTotalAdjust, type TotalAdjustPlan, type TotalColumn } from "./deliveryTotalAdjust";
import { buildDeliveryIssues, type DeliveryIssue } from "./deliveryIssues";
import { roundMoney } from "../../components/numbers";
import type { CatalogEntryValues } from "../../components/search/CatalogPickerModal";

// ─── Логиката на редовете ───────────────────────────────────────────────────
// State на добавените продукти, двупосочните изчисления (въведената стойност
// е източникът на истина — „Пази въведената сума"), авто-цена от последна
// доставка, валидация, суми и „Общо количества".

type Params = {
  mode: "new" | "edit";
  warehouseId: number | null;
  // Има ли изобщо създаден склад — решава текста при липсващ избор
  hasWarehouses: boolean;
  supplierId: number | null;
  vatApplies: boolean; // регистрирана фирма + доставчик с ДДС номер
  productById: Map<number, Product>;
  packagingById: Map<number, Packaging>;
  lastPriceByProduct: Map<number, LastPrice>;
  lastPricesReady: boolean; // useLastPrices().isSuccess
  decimalsOf: (productId: number) => number;
  // Преизчисляване на Нето/Бруто след зареждане (редакция/prefill/чернова)
  recalcNeeded: boolean;
  // продуктите И разфасовките са ЗАРЕДЕНИ (празен списък също е зареден —
  // ресторант без разфасовки иначе никога не виждаше Нето/Бруто при редакция)
  referencesReady: boolean;
  onTouch: () => void; // вдига флага „незапазени промени" + чернова
  focusCell: (productId: number, col: number) => void;
};

export function useDeliveryRowsActions({
  mode,
  warehouseId,
  hasWarehouses,
  supplierId,
  vatApplies,
  productById,
  packagingById,
  lastPriceByProduct,
  lastPricesReady,
  decimalsOf,
  recalcNeeded,
  referencesReady,
  onTouch,
  focusCell,
}: Params) {
  const { t } = useTranslation();

  const [rows, setRows] = useState<Record<number, DeliveryRowDraft>>({});
  // Таблицата показва САМО добавените продукти, в реда на добавяне
  const [addedIds, setAddedIds] = useState<number[]>([]);

  // Складът по подразбиране за нов ред (без ре-абонамент на callback-ите)
  const warehouseIdRef = useRef(warehouseId);
  warehouseIdRef.current = warehouseId;

  const baseOf = useCallback(
    (row: DeliveryRowDraft): number => {
      const pkg = row.packagingId ? packagingById.get(row.packagingId) : undefined;
      return pkg?.quantity ?? 1;
    },
    [packagingById],
  );
  const qtyBase = useCallback(
    (row: DeliveryRowDraft): number => parseDecimal(row.qty) * baseOf(row),
    [baseOf],
  );
  const vatFactor = useCallback(
    (productId: number): number => {
      if (!vatApplies) return 1;
      const rate = productById.get(productId)?.vatRate ?? 0;
      return 1 + rate / 100;
    },
    [vatApplies, productById],
  );

  // ─── Изчисления (двупосочни): ед. цената е ВИНАГИ нето ───────────────────
  // Въведената стойност (`typedSum`: цената или сумата нето/бруто) не се
  // пренаписва; другите две се извеждат от нея (`deliveryRowMath`).
  const recalcRow = useCallback(
    (productId: number, row: DeliveryRowDraft) =>
      recalcDeliveryRow(row, qtyBase(row), vatFactor(productId)),
    [vatFactor, qtyBase],
  );
  // Промяна от оператора: цена / нето / бруто стават въведената стойност;
  // количество и разфасовка я пазят и смятат другите наново.
  const calcRow = useCallback(
    (
      productId: number,
      row: DeliveryRowDraft,
      changed: "qty" | "price" | "net" | "gross" | "pkg",
    ) => {
      if (changed === "price") row.typedSum = null;
      else if (changed === "net" || changed === "gross") row.typedSum = changed;
      row.savedGross = null; // редът е променен → брутото се смята наново
      row.totalAdjust = null; // ръчна промяна → бележката за общото пада
      recalcRow(productId, row);
    },
    [recalcRow],
  );

  const patchRow = useCallback(
    (productId: number, updater: (row: DeliveryRowDraft) => void) => {
      setRows((prev) => {
        const current =
          prev[productId] ?? emptyDeliveryRow(warehouseIdRef.current);
        const next = { ...current };
        updater(next);
        return { ...prev, [productId]: next };
      });
      onTouch();
    },
    [onTouch],
  );

  // ✕ на реда → продуктът излиза изцяло от таблицата
  const removeRow = (productId: number) => {
    setAddedIds((prev) => prev.filter((id) => id !== productId));
    setRows((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    onTouch();
  };

  // Зареждане наведнъж (редакция / prefill / чернова / темплейт)
  const replaceAllRows = useCallback(
    (nextRows: Record<number, DeliveryRowDraft>, order: number[]) => {
      setRows(nextRows);
      setAddedIds(order);
    },
    [],
  );

  // ─── Добавяне от търсачката → фокус в „Количество" на реда ───────────────
  const appendRow = (productId: number) => {
    setAddedIds((prev) => [...prev, productId]);
    // автоматична цена от последната доставка (сива, курсив) още при добавяне
    patchRow(productId, (row) => {
      const lastPrice = lastPriceByProduct.get(productId);
      if (lastPrice != null) {
        row.price = formatUnitPrice(lastPrice.unitPrice);
        row.priceAuto = true;
      }
    });
  };
  const addProduct = (productId: number) => {
    if (!addedIds.includes(productId)) appendRow(productId);
    focusCell(productId, 1);
  };

  // ─── Промени по клетките ─────────────────────────────────────────────────
  const onCellChange = (
    productId: number,
    col: "qty" | "price" | "net" | "gross" | "batch",
    rawValue: string,
  ) => {
    // Числовите клетки приемат САМО цифри, запетая и точка
    const value =
      col === "batch" ? rawValue : rawValue.replace(/[^0-9.,]/g, "");
    patchRow(productId, (row) => {
      if (col === "qty") {
        row.qty = value;
        // първо докосване на реда → автоматична цена от последната доставка
        // (не и при въведена сума — тогава цената се извежда от нея)
        if (!row.typedSum && (row.price === "" || row.priceAuto)) {
          const lastPrice = lastPriceByProduct.get(productId);
          if (lastPrice != null) {
            row.price = formatUnitPrice(lastPrice.unitPrice);
            row.priceAuto = true;
          }
        }
        calcRow(productId, row, "qty");
      } else if (col === "price") {
        row.price = value;
        row.priceAuto = false;
        calcRow(productId, row, "price");
      } else if (col === "net") {
        row.net = value;
        row.priceAuto = false;
        calcRow(productId, row, "net");
      } else if (col === "gross") {
        row.gross = value;
        row.priceAuto = false;
        calcRow(productId, row, "gross");
      } else if (col === "batch") {
        row.batch = value;
      }
    });
  };
  const onCellBlur = (
    productId: number,
    col: "qty" | "price" | "net" | "gross",
  ) => {
    setRows((prev) => {
      const row = prev[productId];
      if (!row) return prev;
      const next = { ...row };
      // Излизането от клетка само подрежда ВЪВЕДЕНАТА стойност (5 знака за
      // цената, 2 за сумата) — не сменя кое е въведено: минаване през
      // изведената цена на ред със записана сума не бива да я пренапише.
      if (col === "qty" && next.qty !== "" && parseDecimal(next.qty) > 0) {
        next.qty = formatQtyInput(parseDecimal(next.qty), decimalsOf(productId));
        recalcRow(productId, next);
      }
      if (col === "price" && next.price !== "" && !next.typedSum) {
        next.price = formatUnitPrice(parseDecimal(next.price));
        recalcRow(productId, next);
      }
      // Сумата се записва ТАКАВА, каквато е въведена (бекендът я пази, а
      // цената извежда) — затова тук само се закръгля до стотинка.
      if (col === "net" && next.net !== "" && next.typedSum === "net") {
        next.net = formatLineSum(parseDecimal(next.net));
        recalcRow(productId, next);
      }
      if (col === "gross" && next.gross !== "" && next.typedSum === "gross") {
        next.gross = formatLineSum(parseDecimal(next.gross));
        recalcRow(productId, next);
      }
      return { ...prev, [productId]: next };
    });
  };

  // ─── „Целият списък" с въведеното в реда ─────────────────────────────────
  // Нов продукт → ред като от търсачката (с авто цена); вече добавен не се
  // дублира — получава само въведеното (за попълнен ред модалът вече е питал).
  // Количеството и цената минават по пътя на РЪЧНОТО въвеждане: к-во, после
  // цена (тя става въведената стойност), излизане от клетката — сумата се
  // извежда от тях (`deliveryRowMath`). Празното поле не пипа реда.
  const addFromCatalog = (ids: number[], values?: CatalogEntryValues) => {
    for (const productId of ids) {
      if (!addedIds.includes(productId)) appendRow(productId);
      const entered = values?.get(productId);
      if (!entered) continue;
      if (entered.qty !== "") {
        onCellChange(productId, "qty", entered.qty);
        onCellBlur(productId, "qty");
      }
      if (entered.price !== undefined) {
        onCellChange(productId, "price", entered.price);
        onCellBlur(productId, "price");
      }
    }
    if (ids[0] != null) focusCell(ids[0], 1);
  };

  // След зареждане (редакция/prefill/чернова) — живи Нето/Бруто
  useEffect(() => {
    if (!recalcNeeded || !referencesReady) return;
    setRows((prev) => {
      const next: Record<number, DeliveryRowDraft> = {};
      for (const [pidStr, row] of Object.entries(prev)) {
        const productId = +pidStr;
        const recalced = { ...row };
        recalcRow(productId, recalced);
        next[productId] = recalced;
      }
      return next;
    });
  }, [recalcNeeded, referencesReady, recalcRow]);

  // Смяна на vatApplies (доставчик/настройка) → преизчисляване от въведеното
  // (при въведена цена/нето се мени брутото, при въведено бруто — нетото)
  const prevVatAppliesRef = useRef(vatApplies);
  useEffect(() => {
    if (prevVatAppliesRef.current === vatApplies) return;
    prevVatAppliesRef.current = vatApplies;
    setRows((prev) => {
      const next: Record<number, DeliveryRowDraft> = {};
      for (const [pidStr, row] of Object.entries(prev)) {
        const productId = +pidStr;
        const recalced = { ...row };
        recalcRow(productId, recalced);
        next[productId] = recalced;
      }
      return next;
    });
  }, [vatApplies, recalcRow]);

  // Смяна на доставчика → презапис на цените САМО на редовете с автоматична
  // цена (неговата последна цена; продукт без негова доставка → празно поле)
  const pricedSupplierRef = useRef<number | null>(null);
  const setPricedSupplier = useCallback((id: number | null) => {
    pricedSupplierRef.current = id;
  }, []);
  useEffect(() => {
    if (mode === "edit") return;
    if (!lastPricesReady) return;
    if (pricedSupplierRef.current === supplierId) return;
    pricedSupplierRef.current = supplierId;
    setRows((prev) => {
      const next: Record<number, DeliveryRowDraft> = {};
      for (const [pidStr, row] of Object.entries(prev)) {
        const productId = +pidStr;
        if (!row.priceAuto) {
          next[productId] = row;
          continue;
        }
        const lastPrice = lastPriceByProduct.get(productId);
        const repriced = {
          ...row,
          price: lastPrice != null ? formatUnitPrice(lastPrice.unitPrice) : "",
        };
        calcRow(productId, repriced, "price");
        next[productId] = repriced;
      }
      return next;
    });
  }, [mode, supplierId, lastPricesReady, lastPriceByProduct, calcRow]);

  // ─── Изведени стойности ──────────────────────────────────────────────────
  const addedProducts = useMemo(
    () =>
      addedIds
        .map((id) => productById.get(id))
        .filter((p): p is Product => !!p),
    [addedIds, productById],
  );
  const addedSet = useMemo(() => new Set(addedIds), [addedIds]);
  const rowOf = useCallback(
    (productId: number): DeliveryRowDraft =>
      rows[productId] ?? emptyDeliveryRow(warehouseId),
    [rows, warehouseId],
  );

  // ─── Валидация (запазена: партида/срок при trackBatch/trackExpiry) ───────
  const touchedEntries = Object.entries(rows).filter(([, row]) =>
    isRowTouched(row),
  );
  const validEntries = touchedEntries.filter(
    ([, row]) =>
      parseDecimal(row.qty) > 0 && row.price !== "" && row.warehouseId != null,
  );
  // Пречките в реда на ЕКРАНА (`addedIds`; евентуален ред извън тях — накрая),
  // не по id като в `Object.entries` — първата сочи първата проблемна клетка
  const screenEntries = (): [number, DeliveryRowDraft][] => {
    const shown = new Set(addedIds);
    const ids = [
      ...addedIds.filter((id) => rows[id]),
      ...Object.keys(rows).map(Number).filter((id) => !shown.has(id)),
    ];
    return ids.map((id) => [id, rows[id]]);
  };
  const computeIssues = (): DeliveryIssue[] =>
    buildDeliveryIssues({
      warehouseId,
      hasWarehouses,
      entries: screenEntries(),
      productById,
      hasValidEntries: validEntries.length > 0,
      t,
    });

  const totalNet = touchedEntries.reduce(
    (sum, [, row]) => sum + parseDecimal(row.net),
    0,
  );
  const totalGross = touchedEntries.reduce(
    (sum, [, row]) => sum + parseDecimal(row.gross),
    0,
  );

  // ─── „Коригирай общото" (deliveryTotalAdjust) ────────────────────────────
  // Планът върху редовете в реда на таблицата; прилагането прави новата сума
  // въведена и помни поетата разлика за бележката под реда.
  const planAdjustTotal = (column: TotalColumn, target: number, current: number) =>
    planTotalAdjust(
      addedIds.filter((id) => rows[id]).map((id): [number, DeliveryRowDraft] => [id, rows[id]]),
      column,
      target,
      current,
    );
  const applyAdjustTotal = (column: TotalColumn, plan: Extract<TotalAdjustPlan, { kind: "apply" }>) =>
    patchRow(plan.productId, (row) => {
      const previous = row.totalAdjust ?? 0;
      row[column] = plan.value;
      row.priceAuto = false;
      calcRow(plan.productId, row, column);
      row.totalAdjust = roundMoney(previous + plan.delta);
    });

  // ─── Сумарни количества по мерни единици (за summary реда) ───────────────
  const unitTotals = useMemo(() => {
    // По единица: сумарно количество + знаците ѝ (за форматиране на чипа).
    const totalsByUnit = new Map<string, { qty: number; dec: number }>();
    for (const [pidStr, row] of Object.entries(rows)) {
      const base =
        parseDecimal(row.qty) *
        (packagingById.get(row.packagingId ?? -1)?.quantity ?? 1);
      if (base <= 0) continue;
      const unit = productById.get(+pidStr)?.unit?.trim() || "—";
      const current = totalsByUnit.get(unit);
      if (current) current.qty += base;
      else totalsByUnit.set(unit, { qty: base, dec: decimalsOf(+pidStr) });
    }
    return [...totalsByUnit.entries()].sort((a, b) => b[1].qty - a[1].qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, productById, packagingById]);

  return {
    rows,
    addedIds,
    addedProducts,
    addedSet,
    rowOf,
    calcRow,
    qtyBase,
    patchRow,
    removeRow,
    replaceAllRows,
    addProduct,
    addFromCatalog,
    onCellChange,
    onCellBlur,
    setPricedSupplier,
    touchedEntries,
    validEntries,
    computeIssues,
    totalNet,
    totalGross,
    unitTotals,
    planAdjustTotal,
    applyAdjustTotal,
  };
}
